import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, update, push } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "./UserStockStatus.css";
import { FaInfoCircle, FaChevronDown, FaChartBar, FaBatteryFull, FaBatteryHalf, FaBatteryEmpty } from "react-icons/fa";

const UserStockStatus = () => {
    const [codes, setCodes] = useState({});
    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState(null);
    const [selected, setSelected] = useState({ codeId: "", siteId: "" });
    const [updatingStatus, setUpdatingStatus] = useState({
        High: false,
        Medium: false,
        Low: false,
    });
    const [companyName, setCompanyName] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [stockCapacity, setStockCapacity] = useState("");
    const [currentStock, setCurrentStock] = useState("");
    const [stockPercentage, setStockPercentage] = useState(null);

    const gifUrl = "/Images/New notification.gif";

    // 🔹 Get logged-in user ID
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserId(user.uid);
            } else {
                console.warn("No user is logged in.");
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    // 🔹 Fetch company name from profileDashboard
    useEffect(() => {
        if (!userId) return;
        const fetchCompanyName = async () => {
            try {
                const db = getDatabase();
                const profileRef = ref(db, `profileDashboard/${userId}/company_name`);
                const snapshot = await get(profileRef);
                if (snapshot.exists()) {
                    setCompanyName(snapshot.val());
                }
            } catch (err) {
                console.error("Error fetching company name:", err);
            }
        };
        fetchCompanyName();
    }, [userId]);

    // 🔹 Fetch codes and sites
    useEffect(() => {
        if (!userId) return;

        const fetchCodes = async () => {
            try {
                const db = getDatabase();
                const codesRef = ref(db, `new-code/${userId}/codes`);
                const snapshot = await get(codesRef);

                if (snapshot.exists()) {
                    const codesData = snapshot.val();
                    setCodes(codesData);

                } else {
                    console.warn("No codes found for this user.");
                }

                setLoading(false);
            } catch (error) {
                console.error("Error fetching codes:", error);
                setLoading(false);
            }
        };

        fetchCodes();
    }, [userId]);

    const getIndianTimestamp = () => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istTime = new Date(now.getTime() + istOffset);
        return istTime.toISOString().replace("T", " ").replace("Z", "");
    };

    // 🔹 Handle stock status update
    const handleUpdateStock = async () => {
        if (!stockCapacity || !currentStock) {
            alert("Please enter both stock capacity and current stock.");
            return;
        }

        const capacity = parseFloat(stockCapacity);
        const current = parseFloat(currentStock);
        if (isNaN(capacity) || isNaN(current) || capacity <= 0) {
            alert("Invalid stock values. Please ensure capacity is greater than 0 and values are numeric.");
            return;
        }

        const percentage = (current / capacity) * 100;
        const status = percentage > 60 ? "High" : percentage >= 30 ? "Medium" : "Low";

        try {
            setUpdatingStatus((prev) => ({ ...prev, [status]: true }));

            const db = getDatabase();
            const siteRef = ref(db, `new-code/${userId}/codes/${selected.codeId}/sites/${selected.siteId}`);
            await update(siteRef, {
                stockStatus: status,
                stockCapacity: parseFloat(stockCapacity),
                currentStock: parseFloat(currentStock),
                stockPercentage: percentage.toFixed(2),
                stockUnit: selected.unit || "MT"
            });


            const siteData = codes[selected.codeId].sites[selected.siteId];

            // 🔹 Prepare notification only for Low or Medium
            if (status === "Low" || status === "Medium") {
                const notificationRef = ref(db, `supplier_notifications/StockStatus/${userId}`);

                let title = "";
                let message = "";

               if (status === "Low") {
    title = "Low Stock Alert";
    message = `${companyName} — Stock at ${siteData.address}, ${siteData.state}${siteData.pincode ? ` - ${siteData.pincode}` : ""} is *Low*. Please contact the client to place a new order ASAP.`;
} else if (status === "Medium") {
    title = "Medium Stock Alert";
    message = `${companyName} — Stock at ${siteData.address}, ${siteData.state}${siteData.pincode ? ` - ${siteData.pincode}` : ""} is *Medium*. Kindly contact the client for reorder.`;
}

                const newNotification = {
                    codeId: selected.codeId,
                    title,
                    message,
                    gifUrl,
                    timestamp: getIndianTimestamp(),
                    seen: false,
                };

                await push(notificationRef, newNotification);
            }

            setCodes((prev) => ({
                ...prev,
                [selected.codeId]: {
                    ...prev[selected.codeId],
                    sites: {
                        ...prev[selected.codeId].sites,
                        [selected.siteId]: {
                            ...prev[selected.codeId].sites[selected.siteId],
                            stockStatus: status,
                            stockCapacity: parseFloat(stockCapacity),
                            currentStock: parseFloat(currentStock),
                            stockPercentage: percentage.toFixed(2),
                        },
                    },
                },
            }));

            setStockPercentage(percentage.toFixed(2));
            alert("Stock status updated successfully.");
        } catch (error) {
            console.error("Error updating stock:", error);
            alert("Failed to update stock status. Please try again.");
        } finally {
            setUpdatingStatus((prev) => ({ ...prev, [status]: false }));
        }
    };

    const getStockFillClass = (percentage) => {
        if (!percentage) return "";
        if (percentage > 60) return "green";
        if (percentage >= 30) return "yellow";
        return "red";
    };

    const getStockMessage = (percentage) => {
        if (!percentage) return { icon: <FaChartBar />, message: "Stock level: Not set" };
        if (percentage > 60) {
            return { icon: <FaBatteryFull />, message: `Stock level: ${percentage}% (Full)` };
        }
        if (percentage >= 30) {
            return { icon: <FaBatteryHalf />, message: `Stock level: ${percentage}% (Medium, please order if required)` };
        }
        return {
            icon: <FaBatteryEmpty />,
            message: `Stock level: ${percentage}% (Low, immediately order to avoid idle site)`
        };
    };

    const stockAlert = () => {
        alert(
            "This page shows your stock status per site.\n\n" +
            "1. Select your site from the dropdown.\n" +
            "2. Enter the site stock capacity and current stock.\n" +
            "3. And also select the MT or Bags .\n" +
            "4. Click Publish to update the stock status.\n\n" +
            "The cylinder will fill based on the stock percentage: above 60% (Green), 30-60% (Orange), below 30% (Red, blinking).\n" +
            "After updating, a notification will be sent to the supplier for Medium or Low stock, and they will contact you soon."
        );
    };

    return (
        <div className="stockStatus-container">
            <div className="stockStatus-card">
                <h2 className="stockStatus-title">
                    Stock Status{" "}
                    <FaInfoCircle
                        style={{ cursor: "pointer", fontSize: "1.2rem", color: "#EA8716", marginTop: '9px' }}
                        onClick={stockAlert}
                    />
                </h2>

                {loading ? (
                    <div className="stockStatus-loading-container">
                        <div className="stockStatus-loading-spinner"></div>
                    </div>
                ) : Object.keys(codes).length === 0 ? (
                    <div className="empty-state">
                        <p className="empty-text">No codes or sites found.</p>
                    </div>
                ) : (
                    <div className="stockStatus-dashboard-content">
                        {/* 🔹 Dropdown for site selection */}
                        <div className="stockStatus-select-container">
                            <label className="stockStatus-select-label">Select Site</label>
                            <div className="custom-dropdown">
                                <div
                                    className="custom-dropdown-toggle"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <span>
                                        {selected.codeId && selected.siteId
                                            ? `${codes[selected.codeId].sites[selected.siteId].address}, ${codes[selected.codeId].sites[selected.siteId].state
                                            }${codes[selected.codeId].sites[selected.siteId].pincode
                                                ? ` (${codes[selected.codeId].sites[selected.siteId].pincode})`
                                                : ""
                                            }`
                                            : "Select a site"}
                                    </span>
                                    <FaChevronDown
                                        style={{
                                            marginLeft: "8px",
                                            fontSize: "0.9rem",
                                            color: "#555",
                                            transition: "transform 0.3s ease",
                                            transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                        }}
                                    />
                                </div>

                                {isDropdownOpen && (
                                    <ul className="custom-dropdown-menu">
                                        {Object.entries(codes).map(([codeId, code]) =>
                                            Object.entries(code.sites).map(([siteId, site]) => {
                                                const optionText = `${site.address}, ${site.state}${site.pincode ? ` (${site.pincode})` : ""
                                                    }`;
                                                return (
                                                    <li
                                                        key={`${codeId}_${siteId}`}
                                                        className="custom-dropdown-item"
                                                        onClick={() => {
                                                            setSelected({ codeId, siteId });
                                                            setIsDropdownOpen(false);
                                                            setStockCapacity(site.stockCapacity || "");
                                                            setCurrentStock(site.currentStock || "");
                                                            setStockPercentage(site.stockPercentage || null);
                                                            setSelected((prev) => ({ ...prev, unit: site.stockUnit || "MT" }));

                                                        }}
                                                    >
                                                        {optionText}
                                                    </li>
                                                );
                                            })
                                        )}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* 🔹 Stock Status Card */}
                        {selected.codeId && selected.siteId ? (
                            <div className="stockStatus-stock-card">
                                <h3 className="stockStatus-stock-title">
                                    <FaChartBar style={{ marginRight: "0.5rem" }} />
                                    Stock Status
                                </h3>
                                <div className="stockStatus-stock-details">
                                    <div className="stock-visual-container">
                                        <div className="stock-visual-row">
                                            <div className="stock-visual">
                                                <div className="stockStatus-cylindrical">
                                                    <div
                                                        className={`cylinder-fill ${getStockFillClass(stockPercentage)}`}
                                                        style={{ height: stockPercentage ? `${stockPercentage}%` : "0%" }}
                                                    ></div>
                                                </div>
                                            </div>
                                            <div className="stock-message">
                                                <div className="stock-message-content">
                                                    <span className="stock-message-icon">
                                                        {getStockMessage(stockPercentage).icon}
                                                    </span>
                                                    <p className="status-text">
                                                        {getStockMessage(stockPercentage).message}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="stock-input-container">
                                            <div className="input-group">
                                                <label className="stockStatus-input-label">Site Stock Capacity</label>
                                                <div className="input-with-unit">
                                                    <input
                                                        type="number"
                                                        value={stockCapacity}
                                                        onChange={(e) => setStockCapacity(e.target.value)}
                                                        className="stockStatus-input"
                                                        placeholder="Enter capacity"
                                                        min="0"
                                                        onWheel={(e) => e.target.blur()}
                                                    />

                                                </div>
                                            </div>

                                            <div className="input-group">
                                                <label className="stockStatus-input-label">Current Stock</label>
                                                <div className="input-with-unit">
                                                    <input
                                                        type="number"
                                                        value={currentStock}
                                                        onChange={(e) => setCurrentStock(e.target.value)}
                                                        className="stockStatus-input"
                                                        placeholder="Enter current stock"
                                                        min="0"
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    <select
                                                        value={selected.unit || "MT"}
                                                        onChange={(e) => setSelected((prev) => ({ ...prev, unit: e.target.value }))}
                                                        className="unit-select"
                                                    >
                                                        <option value="MT">MT</option>
                                                        <option value="Bags">Bags</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <button
                                                onClick={handleUpdateStock}
                                                className={`action-button publish ${updatingStatus.High || updatingStatus.Medium || updatingStatus.Low ? "disabled" : ""
                                                    }`}
                                                disabled={updatingStatus.High || updatingStatus.Medium || updatingStatus.Low}
                                            >
                                                {updatingStatus.High || updatingStatus.Medium || updatingStatus.Low ? (
                                                    <span className="button-loading">
                                                        <span className="button-spinner"></span>
                                                        Publishing...
                                                    </span>
                                                ) : (
                                                    "Publish"
                                                )}
                                            </button>


                                        </div>
                                        <p className="conversion-info">
                                            *Note: 20 Bags = 1 MT
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="select-site-message">
                                <p>Please select your site to report your stock.</p>
                            </div>

                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserStockStatus;